'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useAtom } from 'jotai';
import { tv, type VariantProps } from 'tailwind-variants';
import classNames from 'classnames';
import { showGenesysAtom } from '@/state/atoms';

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

interface GenesysFormProps extends FormVariantProps {}

export default function GenesysForm({ bgColor = 'blue' }: GenesysFormProps) {
  const getInitialFormState = () => {
    const savedState =
      typeof window !== 'undefined' && window.localStorage
        ? window.localStorage.getItem('showGenesysForm')
        : null;
    return savedState ? JSON.parse(savedState) : false;
  };
  const [showForm, setShowForm] = useState<boolean>(getInitialFormState);
  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('showGenesysForm', JSON.stringify(showForm));
    }
  }, [showForm]);

  const getInitialButtonState = () => {
    const savedState =
      typeof window !== 'undefined' && window.localStorage
        ? window.localStorage.getItem('showGenesysButton')
        : null;
    return savedState ? JSON.parse(savedState) : true;
  };
  const [showButton, setShowButton] = useState<boolean>(getInitialButtonState);
  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(
        'showGenesysButton',
        JSON.stringify(showButton),
      );
    }
  }, [showButton]);

  //form state
  const getInitialFormValuesState = () => {
    const savedState =
      typeof window !== 'undefined' && window.localStorage
        ? window.localStorage.getItem('GenesysFormValues')
        : null;
    return savedState
      ? JSON.parse(savedState)
      : { firstName: '', lastName: '', email: '', phone: '' };
  };
  const [formValues, setFormValues] = useState(getInitialFormValuesState);
  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(
        'GenesysFormValues',
        JSON.stringify(formValues),
      );
    }
  }, [formValues]);

  const getInitialFormErrorsState = () => {
    const savedState =
      typeof window !== 'undefined' && window.localStorage
        ? window.localStorage.getItem('GenesysFormErrors')
        : null;
    return savedState
      ? JSON.parse(savedState)
      : { firstName: '', lastName: '', email: '', phone: '' };
  };
  const [formErrors, setFormErrors] = useState(getInitialFormErrorsState);
  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(
        'GenesysFormErrors',
        JSON.stringify(formErrors),
      );
    }
  }, [formErrors]);

  const [showGenesys, _] = useAtom(showGenesysAtom);

  // show/hide genesys chat based on whether genesys form is displayed & if the current time is within/outside of normal working hours
  useEffect(() => {
    function getOriginalGenesysButtonDisplayValue(
      showButton: boolean,
      showGenesys: boolean,
    ) {
      return showButton ? 'none' : showGenesys ? 'block' : 'none';
    }

    let mutationObserver: MutationObserver | null;
    const originalGenesysButton = document.getElementById('genesys-messenger');

    if (originalGenesysButton) {
      //'#genesys-messenger' is already in the dom
      originalGenesysButton.style.display =
        getOriginalGenesysButtonDisplayValue(showButton, showGenesys);
    } else {
      //'#genesys-messenger' is yet to be added to the dom
      mutationObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'childList') {
            for (const addedNode of mutation.addedNodes) {
              if (addedNode instanceof HTMLElement) {
                if (addedNode.id === 'genesys-messenger') {
                  addedNode.style.display =
                    getOriginalGenesysButtonDisplayValue(
                      showButton,
                      showGenesys,
                    );
                }
              }
            }
          }
        }
      });
      mutationObserver.observe(document.body, { childList: true });
    }

    return () => {
      mutationObserver?.disconnect();
    };
  }, [showButton, showGenesys]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prev: any) => ({ ...prev, [name]: value }));
  };

  // close genesys form when user clicks outside of it
  const componentRef = useRef<HTMLDivElement>(null);
  const genesysChatButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const handleClickOutsideComponent = (e: MouseEvent) => {
      if (
        componentRef.current &&
        !componentRef.current.contains(e.target as Node) &&
        !genesysChatButtonRef.current?.contains(e.target as Node)
      ) {
        e.preventDefault();
        setShowForm((prev: any) => !prev);
      }
    };

    document.addEventListener('mousedown', handleClickOutsideComponent);

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideComponent);
    };
  }, []);

  const validateFormField = (e: React.FocusEvent<HTMLInputElement>) => {
    const name = e.target.name;
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const phoneRegex = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
    switch (name) {
      case 'firstName':
        if (!formValues.firstName) {
          setFormErrors((prev: any) => ({
            ...prev,
            firstName: 'First name is required.',
          }));
        } else {
          setFormErrors((prev: any) => ({ ...prev, firstName: '' }));
        }
        break;
      case 'lastName':
        if (!formValues.lastName) {
          setFormErrors((prev: any) => ({
            ...prev,
            lastName: 'Last name is required.',
          }));
        } else {
          setFormErrors((prev: any) => ({ ...prev, lastName: '' }));
        }
        break;
      case 'email':
        if (!formValues.email) {
          setFormErrors((prev: any) => ({
            ...prev,
            email: 'Email is required.',
          }));
        } else if (!emailRegex.test(formValues.email)) {
          setFormErrors((prev: any) => ({
            ...prev,
            email: 'Invalid email format.',
          }));
        } else {
          setFormErrors((prev: any) => ({ ...prev, email: '' }));
        }
        break;
      case 'phone':
        if (formValues.phone && !phoneRegex.test(formValues.phone)) {
          setFormErrors((prev: any) => ({
            ...prev,
            phone: 'Invalid phone number format.',
          }));
        } else {
          setFormErrors((prev: any) => ({ ...prev, phone: '' }));
        }
        break;
      default:
        setFormErrors((prev: any) => ({ ...prev }));
    }
  };

  const validateForm = (values: FormValues) => {
    const errors = { firstName: '', lastName: '', email: '', phone: '' };
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
    const phoneRegex = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/i;
    if (!values.firstName) {
      errors.firstName = 'First name is required.';
    }
    if (!values.lastName) {
      errors.lastName = 'Last name is required.';
    }
    if (!values.email) {
      errors.email = 'Email name is required.';
    } else if (!emailRegex.test(values.email)) {
      errors.email = 'Invalid email format.';
    }
    if (formValues.phone && !phoneRegex.test(values.phone)) {
      errors.phone = 'Invalid phone number format.';
    }
    return errors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors(validateForm(formValues));

    const formInvalid = Object.values(formErrors).some((value) => value !== '');

    if (
      formValues.firstName !== '' &&
      formValues.lastName !== '' &&
      formValues.email !== '' &&
      !formInvalid &&
      window.Genesys
    ) {
      // valid form
      setShowButton(false);

      window.Genesys('subscribe', 'Database.Ready', function () {
        window.Genesys('command', 'Database.set', {
          messaging: {
            customAttributes: {
              firstname: formValues.firstName,
              lastname: formValues.lastName,
              email: formValues.email,
              phone: formValues.phone,
              name: `${formValues.firstName} ${formValues.lastName}`,
            },
          },
        });
      });

      window.Genesys('subscribe', 'Messenger.opened', function () {
        window.Genesys('command', 'Messenger.openConversation');
      });
      window.Genesys('subscribe', 'Messenger.closed', function () {});
      window.Genesys(
        'command',
        'Messenger.open',
        {},
        function () {
          //run on success
          setShowForm(false);
        },
        function () {
          //run on error (ie: if messenger is already opened)
          setShowForm(false);
        },
      );
    }
  };

  //chat button
  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setShowForm((prev: any) => !prev);
  };

  const handleImplicitSubmissionPrevention = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  return (
    <>
      {showGenesys && showForm && (
        <div
          ref={componentRef}
          className={classNames(
            'fixed bottom-[0] left-[0] right-[0] top-[0] z-[999999999] w-full overflow-y-auto rounded-tl-[4px] rounded-tr-[4px] bg-[#ffffff] text-[#fff] sm:fixed sm:bottom-[6rem] sm:left-auto sm:right-[18px] sm:top-auto sm:max-h-[calc(100vh-100px)] sm:w-[412px] sm:rounded-[4px] sm:border-[1px]',
            `sm:${formVariants({ bgColor })}`,
          )}
        >
          <div
            className={classNames(
              'flex justify-between',
              formVariants({ bgColor }),
            )}
          >
            <div>
              <h2 className="px-[1rem] pt-[1rem] !text-[1.33929rem]">
                Welcome
              </h2>
              <h3 className="px-[1rem] !text-[0.9375rem]">
                We&apos;re here to help
              </h3>
            </div>
            <GenesysMobileCloseButton
              onButtonClick={handleButtonClick}
              bgColor={bgColor}
            />
          </div>
          <form className="" onSubmit={handleSubmit}>
            <div
              className={classNames(
                'grid grid-cols-1 gap-[0.5rem] p-[1rem]',
                formVariants({ bgColor }),
              )}
            >
              <div>
                <div className="grid grid-cols-1">
                  <label htmlFor="genesysFirstName">
                    First Name:{' '}
                    <span className="text-[0.85rem] text-[#ffffff]">
                      (required)
                    </span>
                  </label>
                  <input
                    className={classNames(
                      'rounded-[4px] px-[1rem] py-[0.5rem] text-[#000000]',
                      formErrors.firstName && 'rounded-bl-[0]',
                    )}
                    id="genesysFirstName"
                    type="text"
                    name="firstName"
                    placeholder="First Name"
                    value={formValues.firstName}
                    onChange={handleChange}
                    onBlur={validateFormField}
                  />
                </div>
                {formErrors.firstName && (
                  <p className="w-fit rounded-b-[4px] bg-[#ffffff] px-[0.5rem] py-[3px] text-[0.85rem] font-semibold leading-none text-[#A12E12]">
                    {formErrors.firstName}
                  </p>
                )}
              </div>
              <div>
                <div className="grid grid-cols-1">
                  <label htmlFor="genesysLastName">
                    Last Name{' '}
                    <span className="text-[0.85rem] text-[#ffffff]">
                      (required)
                    </span>
                  </label>
                  <input
                    className={classNames(
                      'rounded-[4px] px-[1rem] py-[0.5rem] text-[#000000]',
                      formErrors.firstName && 'rounded-bl-[0]',
                    )}
                    id="genesysLastName"
                    type="text"
                    name="lastName"
                    placeholder="Last Name"
                    value={formValues.lastName}
                    onChange={handleChange}
                    onBlur={validateFormField}
                  />
                </div>
                {formErrors.lastName && (
                  <p className="w-fit rounded-b-[4px] bg-[#ffffff] px-[0.5rem] py-[3px] text-[0.85rem] font-semibold leading-none text-[#A12E12]">
                    {formErrors.lastName}
                  </p>
                )}
              </div>
              <div>
                <div className="grid-cols-1] grid">
                  <label htmlFor="genesysEmail">
                    Email{' '}
                    <span className="text-[0.85rem] text-[#ffffff]">
                      (required)
                    </span>
                  </label>
                  <input
                    className={classNames(
                      'rounded-[4px] px-[1rem] py-[0.5rem] text-[#000000]',
                      formErrors.firstName && 'rounded-bl-[0]',
                    )}
                    id="genesysEmail"
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={formValues.email}
                    onChange={handleChange}
                    onBlur={validateFormField}
                  />
                </div>
                {formErrors.email && (
                  <p className="w-fit rounded-b-[4px] bg-[#ffffff] px-[0.5rem] py-[3px] text-[0.85rem] font-semibold leading-none text-[#A12E12]">
                    {formErrors.email}
                  </p>
                )}
              </div>
              <div>
                <div className="grid grid-cols-1">
                  <label htmlFor="genesysPhone">
                    Phone <span className="text-[0.85rem]">(optional)</span>
                  </label>
                  <input
                    className={classNames(
                      'rounded-[4px] px-[1rem] py-[0.5rem] text-[#000000]',
                      formErrors.firstName && 'rounded-bl-[0]',
                    )}
                    id="genesysPhone"
                    type="tel"
                    name="phone"
                    placeholder="Phone"
                    value={formValues.phone}
                    onChange={handleChange}
                    onBlur={validateFormField}
                    onKeyDown={handleImplicitSubmissionPrevention}
                  />
                </div>
                {formErrors.phone && (
                  <p className="w-fit rounded-b-[4px] bg-[#ffffff] px-[0.5rem] py-[3px] text-[0.85rem] font-semibold leading-none text-[#A12E12]">
                    {formErrors.phone}
                  </p>
                )}
              </div>
            </div>
            <div className="bg-[#fff] p-[1rem]">
              <input
                className={classNames(
                  'rounded-[0.25rem] px-[1rem] py-[0.5rem]',
                  formVariants({ bgColor }),
                )}
                type="submit"
                value="Submit"
              />
            </div>
          </form>
        </div>
      )}
      {showGenesys && showButton && (
        <GenesysChatButton
          onButtonClick={handleButtonClick}
          bgColor={bgColor}
          buttonRef={genesysChatButtonRef}
        />
      )}
    </>
  );
}

interface GenesysChatButtonProps extends FormVariantProps {
  readonly onButtonClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  buttonRef: React.Ref<HTMLButtonElement>;
}

function GenesysChatButton({
  onButtonClick,
  bgColor,
  buttonRef,
}: GenesysChatButtonProps) {
  return (
    <button
      ref={buttonRef}
      onClick={onButtonClick}
      className={classNames(
        'shadow-genesys-button fixed bottom-[22px] right-[19px] z-[99999999] rounded-[50%] p-[15px] shadow-[rgba(0,0,0,0.2)_0px_3px_5px_-2px,rgba(0,0,0,0.14)_0px_1px_4px_2px,rgba(0,0,0,0.12)_0px_1px_4px_1px]',
        formVariants({ bgColor }),
      )}
    >
      <svg
        className="Cx-MuiSvgIcon-root Cx-MuiSvgIcon-fontSizeMedium cx-messenger-1fqukdu w-[26px]"
        focusable="false"
        aria-hidden="true"
        viewBox="0 0 24 24"
        data-testid="ChatIcon"
        fill="#ffffff"
      >
        <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2M6 9h12v2H6zm8 5H6v-2h8zm4-6H6V6h12z"></path>
      </svg>
    </button>
  );
}

interface GenesysMobileCloseButtonProps extends FormVariantProps {
  readonly onButtonClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

function GenesysMobileCloseButton({
  onButtonClick,
  bgColor,
}: GenesysMobileCloseButtonProps) {
  return (
    <div
      className={classNames(
        'mr-[2.5px] w-fit p-[15px] sm:hidden',
        formVariants({ bgColor }),
      )}
    >
      <button
        onClick={onButtonClick}
        className="Cx-MuiButtonBase-root Cx-MuiIconButton-root Cx-MuiIconButton-colorInherit Cx-MuiIconButton-sizeMedium admin-prevent-pointer cx-messenger-1djes9v"
        type="button"
        aria-label="Minimize the Messenger"
      >
        <svg
          className="Cx-MuiSvgIcon-root Cx-MuiSvgIcon-fontSizeDefault cx-messenger-8hqv1y w-[26px]"
          focusable="false"
          aria-hidden="true"
          viewBox="0 0 24 24"
          data-testid="RemoveIcon"
          fill="#ffffff"
        >
          <path stroke="#ffffff" d="M19 13H5v-2h14z"></path>
        </svg>
        <span className="MuiTouchRipple-root cx-messenger-w0pj6f"></span>
      </button>
    </div>
  );
}

const formVariants = tv({
  base: '',
  variants: {
    bgColor: {
      blue: 'bg-[#266090]',
      black: 'bg-[#231f19]',
    },
  },
  defaultVariants: {
    bgColor: 'blue',
  },
});

type FormVariantProps = VariantProps<typeof formVariants>;
